import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useHistory, useParams } from "react-router-dom";
import { PencilSimple, X } from "phosphor-react";
import { StackAnalysisContext } from "context/stack_analysis";
import Badge from "components/elements/Badge";
import { getFarm, getFarms } from "api/Farm";
import { UserContext } from "context/user";
import { toast } from "react-toastify";
import CancelProcessModal from "components/Modal/CancelProcessModal";
import SelectFarmModal from "../SelectFarmModal";
import "./index.scss";

const StackAnalysisToolbar = () => {
  const { farmId } = useParams();
  const { token } = useContext(UserContext);
  const { state } = useContext(StackAnalysisContext);
  const history = useHistory();
  const [farmInfo, setFarminfo] = useState(null);
  const [userFarms, setUserFarms] = useState(null);
  const [filterFarms, setFilterFarms] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchFarmName, setSearchFarmName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState("");
  const { currentPage } = state;
  const fetchIdRef = useRef(0);

  const goBack = () => {
    history.push(`/farms/${farmId}`);
    window.location.reload();
  };

  const fetchFarmData = useCallback(async () => {
    try {
      const farmDetails = await getFarm(farmId, token);
      setFarminfo(farmDetails);
    } catch (e) {
      toast.error("Please contact support@datafarming.com.au");
    }
  }, [token, farmId]);

  const fetchUserFarms = useCallback(async () => {
    fetchIdRef.current += 1;
    const fetchId = fetchIdRef.current;
    if (fetchId === fetchIdRef.current && token) {
      const limit = 100;
      const skip = 0;
      try {
        const query = `skip=${skip}&limit=${limit}`;
        setIsLoading(true);
        const farmList = await getFarms(query, token);
        setUserFarms(farmList?.features);
        setFilterFarms(farmList?.features);
        setIsLoading(false);
      } catch (e) {
        toast.error("Please contact support@datafarming.com.au");
        setIsLoading(false);
      }
    }
  }, [token, setUserFarms]);

  const handleSearch = (search) => {
    let filteredFarms = {};
    filteredFarms = userFarms?.filter((farm) => {
      if (search === "") return userFarms;
      return farm?.properties?.farm_name
        ?.toLowerCase()
        .includes(search.toLowerCase());
    });
    setSearchFarmName(search);
    setFilterFarms(filteredFarms);
  };

  const handleOpenModal = () => {
    setSearchFarmName("");
    setIsOpen(true);
  };
  const handleCloseModal = () => {
    setIsCancelled(false);
  };

  useEffect(() => {
    if (!token) return;
    fetchFarmData();
    fetchUserFarms();
  }, [fetchFarmData, fetchUserFarms, token]);

  const handleSelectNewFarm = (farm) => {
    setIsCancelled(true);
    setIsOpen(false);
    setRedirectUrl(`/stack-analysis/${farm?.id}`);
  };

  return (
    <>
      <section className="transaction-headings">
        <div className="transaction-nav">
          <span
            className="link"
            aria-hidden
            onKeyDown={() => goBack()}
            onClick={() => goBack()}
          >
            <X size={20} color="#292b33" weight="bold" />
          </span>
          <h1 data-testid="title" className="title">
            Stack Analysis for
            <div
              className="farm-name"
              onClick={() => handleOpenModal()}
              aria-hidden
            >
              {farmInfo?.properties?.farm_name}{" "}
            </div>
            <PencilSimple size={20} color="#00CA52" />
          </h1>
        </div>
        <div className="stepper-area">
          <div className="step">
            <Badge
              style={currentPage !== "fields" ? "outlined" : "contained"}
              number="1"
            />
            <p className="label">Fields</p>
          </div>
          <div className="step">
            <Badge
              style={currentPage !== "seasons" ? "outlined" : "contained"}
              number="2"
            />
            <p className="label">Season</p>
          </div>
          <div className="step">
            <Badge
              style={currentPage !== "captures" ? "outlined" : "contained"}
              number="3"
            />
            <p className="label">Captures</p>
          </div>
          <div className="step">
            <Badge
              style={currentPage !== "review" ? "outlined" : "contained"}
              number="4"
            />
            <p className="label">Review</p>
          </div>
        </div>
      </section>
      {farmInfo ? (
        <SelectFarmModal
          farms={filterFarms}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          handleSelectNewFarm={handleSelectNewFarm}
          handleSearch={handleSearch}
          searchFarmName={searchFarmName}
          isLoading={isLoading}
        />
      ) : null}
      <div className="stack-sticky">
        <p className="text">Do you want to book outside of Australia? </p>
        <a className="link" href="mailto:support@datafarming.com">
          Contact Us
        </a>
      </div>
      <CancelProcessModal
        isOpen={isCancelled}
        redirectUrl={redirectUrl}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default StackAnalysisToolbar;
